import { useState, useEffect } from 'react';
import { useModels } from '@/hooks/use-models-v2';
import { Button } from '@/components/ui/Button';

interface NewConversationModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (title: string, modelId: string) => void;
  creating?: boolean;
}

export function NewConversationModal({
  open,
  onClose,
  onCreate,
  creating = false,
}: NewConversationModalProps) {
  const [selectedModelId, setSelectedModelId] = useState('');
  const [title, setTitle] = useState('');
  const { data: modelsData } = useModels();
  const models = modelsData?.data ?? [];

  useEffect(() => {
    if (open) {
      setSelectedModelId('');
      setTitle('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const ready = selectedModelId && title.trim();

  const handleCreate = () => {
    if (ready) onCreate(title.trim(), selectedModelId);
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-void/70 z-50"
        onClick={onClose}
        data-testid="modal-overlay"
      />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md bg-panel-bg border border-panel-border rounded-[3px]">
        <header className="px-4 py-3 border-b border-panel-border">
          <span className="text-micro font-mono font-semibold text-text-muted uppercase tracking-widest">
            NEW ANALYSIS
          </span>
        </header>
        <div className="p-4 flex flex-col gap-4">
          <div>
            <label className="text-xs font-mono text-text-muted uppercase tracking-[0.5px] block mb-1.5">
              MODEL
            </label>
            <select
              aria-label="Model"
              value={selectedModelId}
              onChange={(e) => setSelectedModelId(e.target.value)}
              className="w-full py-2.5 px-3 bg-input-bg border border-panel-border rounded-[2px] text-sm font-mono text-text-bright appearance-none cursor-pointer outline-none focus:border-accent/30"
            >
              <option value="">Select a model...</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} v{m.version} —{' '}
                  {/* variables-counter-coherence Inc-6 (2026-05-20): prefer
                      the addressable count (scalar + table cells) so this
                      matches the builder's TRACKED total. Falls back to
                      `inputCount` for pre-Inc-1 registry rows. */}
                  {m.addressableInputCount ?? m.inputCount} inputs,{' '}
                  {m.outputCount} outputs
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono text-text-muted uppercase tracking-[0.5px] block mb-1.5">
              DEAL TITLE
            </label>
            <input
              type="text"
              aria-label="Deal Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., CrossPointe Value-Add 120-Unit"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && ready) handleCreate();
              }}
              className="w-full py-2.5 px-3 bg-input-bg border border-panel-border rounded-[2px] text-sm font-mono text-text-bright placeholder:text-text-dim outline-none focus:border-accent/30"
            />
          </div>
        </div>
        <footer className="flex justify-end gap-2 px-4 py-3 border-t border-panel-border">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={!ready || creating}
          >
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </footer>
      </div>
    </>
  );
}
